/**
 * Dados MOCADOS das Metricas (MET-01..04).
 *
 * Nao ha rota de API de metricas ainda: a tela le deste modulo. Os numeros sao
 * coerentes entre si por periodo (throughput x lead time x SLA), para que a
 * leitura da tela faca sentido durante a validacao do desenho.
 *
 * Vocabulario: "Item de Trabalho"/"Itens de Trabalho". Nao existe Sprint.
 */

/** Semantica de leitura de um numero — nunca comunicada SO por cor na tela. */
export type MetricTone = 'neutral' | 'success' | 'warning' | 'danger'

/** Cartao de indicador do topo do painel. */
export type MetricCard = {
  id: string
  label: string
  /** Valor ja formatado (a tela nao faz conta). */
  value: string
  /** Unidade curta exibida ao lado do valor ("dias", "%", "itens/semana"). */
  unit: string
  /** Frase que explica de onde o numero sai. */
  hint: string
  tone: MetricTone
  /** Comparacao com o periodo anterior; `null` quando nao ha base de comparacao. */
  delta: { label: string; direction: 'up' | 'down' | 'flat'; isGood: boolean } | null
}

/** Ponto de uma serie temporal (grafico de linha). */
export type SeriesPoint = { label: string; value: number }

/** Barra de um grafico de barras. */
export type BarDatum = { label: string; value: number; tone?: MetricTone }

export type LineSeries = {
  title: string
  /** Explica o que a linha mede e como ela foi apurada. */
  caption: string
  /** Sufixo dos numeros ("d", "%", ...). */
  unit: string
  points: readonly SeriesPoint[]
  /** Linha horizontal de referencia (meta), opcional. */
  target?: { value: number; label: string }
}

export type BarSeries = {
  title: string
  caption: string
  unit: string
  bars: readonly BarDatum[]
}

/** Aba Fluxo — como o trabalho atravessa o sistema. */
export type FlowPanel = {
  cards: readonly MetricCard[]
  leadTime: LineSeries
  throughput: BarSeries
  cycleTimeByType: BarSeries
  wipByStatus: BarSeries
}

/** Linha da tabela de SLA por prioridade. */
export type SlaByPriorityRow = {
  priority: string
  targetLabel: string
  total: number
  onTime: number
  breached: number
}

/** Aba Demandas — entrada, triagem e cumprimento de SLA. */
export type DemandsPanel = {
  cards: readonly MetricCard[]
  slaCompliance: LineSeries
  byChannel: BarSeries
  byStage: BarSeries
  slaByPriority: readonly SlaByPriorityRow[]
}

/** Progresso de uma Solucao do portfolio. */
export type PortfolioProgressRow = {
  solution: string
  owner: string
  done: number
  total: number
  /** Itens parados aguardando decisao ou dependencia. */
  blocked: number
}

/** Aba Portfolio — avanco da hierarquia Solucao -> Produto -> ... -> Acao. */
export type PortfolioPanel = {
  cards: readonly MetricCard[]
  progressBySolution: readonly PortfolioProgressRow[]
  deliveryTrend: LineSeries
  itemsByLevel: BarSeries
}

export type AnalyticsSnapshot = {
  periodLabel: string
  /** Quando o consolidado foi calculado (texto pronto para exibir). */
  generatedAtLabel: string
  flow: FlowPanel
  demands: DemandsPanel
  portfolio: PortfolioPanel
}

export type AnalyticsPeriodId = '7d' | '30d' | '90d' | '12m'

/** Periodos oferecidos no seletor da tela. */
export const ANALYTICS_PERIODS: readonly { value: AnalyticsPeriodId; label: string }[] = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: '12m', label: 'Últimos 12 meses' },
]

export const DEFAULT_ANALYTICS_PERIOD: AnalyticsPeriodId = '30d'

const SNAPSHOT_30D: AnalyticsSnapshot = {
  periodLabel: 'Últimos 30 dias',
  generatedAtLabel: 'Consolidado em 08/08/2026, 06:00',
  flow: {
    cards: [
      {
        id: 'lead-time',
        label: 'Lead time médio',
        value: '9,4',
        unit: 'dias',
        hint: 'Da criação do Item de Trabalho até a entrada em Concluído.',
        tone: 'warning',
        delta: { label: '+1,2 dia vs. período anterior', direction: 'up', isGood: false },
      },
      {
        id: 'cycle-time',
        label: 'Cycle time médio',
        value: '4,1',
        unit: 'dias',
        hint: 'Do primeiro movimento para Em execução até Concluído.',
        tone: 'success',
        delta: { label: '−0,6 dia vs. período anterior', direction: 'down', isGood: true },
      },
      {
        id: 'throughput',
        label: 'Throughput',
        value: '31',
        unit: 'itens/semana',
        hint: 'Média de Itens de Trabalho concluídos por semana no período.',
        tone: 'success',
        delta: { label: '+4 itens vs. período anterior', direction: 'up', isGood: true },
      },
      {
        id: 'wip',
        label: 'Trabalho em andamento',
        value: '46',
        unit: 'itens',
        hint: 'Itens de Trabalho fora de Backlog e de Concluído neste momento.',
        tone: 'warning',
        delta: { label: '+9 itens vs. período anterior', direction: 'up', isGood: false },
      },
    ],
    leadTime: {
      title: 'Lead time por semana',
      caption: 'Média de dias entre a criação e a conclusão dos Itens de Trabalho encerrados na semana.',
      unit: 'd',
      target: { value: 8, label: 'Meta: 8 dias' },
      points: [
        { label: '08/07', value: 8.1 },
        { label: '15/07', value: 8.9 },
        { label: '22/07', value: 10.2 },
        { label: '29/07', value: 9.6 },
        { label: '05/08', value: 9.4 },
      ],
    },
    throughput: {
      title: 'Itens de Trabalho concluídos por semana',
      caption: 'Contagem de itens que entraram em Concluído em cada semana do período.',
      unit: 'itens',
      bars: [
        { label: '08/07', value: 27 },
        { label: '15/07', value: 33 },
        { label: '22/07', value: 24, tone: 'warning' },
        { label: '29/07', value: 36, tone: 'success' },
        { label: '05/08', value: 35 },
      ],
    },
    cycleTimeByType: {
      title: 'Cycle time por tipo de Item de Trabalho',
      caption: 'Tempo médio em execução, por tipo. Ação é o tipo mais rápido; Módulo concentra dependências.',
      unit: 'd',
      bars: [
        { label: 'Ação', value: 1.8, tone: 'success' },
        { label: 'Funcionalidade', value: 4.3 },
        { label: 'Módulo', value: 9.7, tone: 'warning' },
        { label: 'Correção', value: 2.4, tone: 'success' },
        { label: 'Melhoria', value: 5.1 },
      ],
    },
    wipByStatus: {
      title: 'Trabalho em andamento por status',
      caption: 'Distribuição atual dos Itens de Trabalho que já saíram do Backlog e ainda não foram concluídos.',
      unit: 'itens',
      bars: [
        { label: 'Em análise', value: 11 },
        { label: 'Em execução', value: 19 },
        { label: 'Em revisão', value: 9 },
        { label: 'Bloqueado', value: 7, tone: 'danger' },
      ],
    },
  },
  demands: {
    cards: [
      {
        id: 'sla-compliance',
        label: 'SLA cumprido',
        value: '92',
        unit: '%',
        hint: 'Demandas respondidas dentro do prazo do tipo de serviço.',
        tone: 'success',
        delta: { label: '+3 p.p. vs. período anterior', direction: 'up', isGood: true },
      },
      {
        id: 'open-demands',
        label: 'Demandas em aberto',
        value: '58',
        unit: 'demandas',
        hint: 'Demandas registradas e ainda não encerradas.',
        tone: 'neutral',
        delta: { label: '−4 vs. período anterior', direction: 'down', isGood: true },
      },
      {
        id: 'triage-time',
        label: 'Tempo médio de triagem',
        value: '6,2',
        unit: 'horas',
        hint: 'Do registro da demanda até a definição de área e prioridade.',
        tone: 'warning',
        delta: { label: '+1,1 hora vs. período anterior', direction: 'up', isGood: false },
      },
      {
        id: 'breached',
        label: 'Demandas com SLA estourado',
        value: '9',
        unit: 'demandas',
        hint: 'Demandas que passaram do prazo de resposta no período.',
        tone: 'danger',
        delta: { label: '−2 vs. período anterior', direction: 'down', isGood: true },
      },
    ],
    slaCompliance: {
      title: 'Cumprimento de SLA por semana',
      caption: 'Percentual de demandas respondidas dentro do prazo em cada semana.',
      unit: '%',
      target: { value: 95, label: 'Meta: 95%' },
      points: [
        { label: '08/07', value: 88 },
        { label: '15/07', value: 90 },
        { label: '22/07', value: 86 },
        { label: '29/07', value: 94 },
        { label: '05/08', value: 92 },
      ],
    },
    byChannel: {
      title: 'Demandas por canal de entrada',
      caption: 'Origem das demandas registradas no período.',
      unit: 'demandas',
      bars: [
        { label: 'Portal', value: 74 },
        { label: 'E-mail', value: 41 },
        { label: 'Reunião', value: 18 },
        { label: 'Telefone', value: 12 },
        { label: 'API', value: 7 },
      ],
    },
    byStage: {
      title: 'Demandas por estágio',
      caption: 'Onde estão as demandas em aberto neste momento.',
      unit: 'demandas',
      bars: [
        { label: 'Nova', value: 14 },
        { label: 'Em triagem', value: 16, tone: 'warning' },
        { label: 'Aguardando solicitante', value: 11 },
        { label: 'Em atendimento', value: 13 },
        { label: 'Pronta p/ conversão', value: 4, tone: 'success' },
      ],
    },
    slaByPriority: [
      { priority: 'Crítica', targetLabel: 'Responder em 4 h', total: 18, onTime: 15, breached: 3 },
      { priority: 'Alta', targetLabel: 'Responder em 8 h', total: 44, onTime: 40, breached: 4 },
      { priority: 'Média', targetLabel: 'Responder em 24 h', total: 61, onTime: 59, breached: 2 },
      { priority: 'Baixa', targetLabel: 'Responder em 72 h', total: 29, onTime: 29, breached: 0 },
    ],
  },
  portfolio: {
    cards: [
      {
        id: 'active-solutions',
        label: 'Soluções ativas',
        value: '4',
        unit: 'soluções',
        hint: 'Soluções com pelo menos um Item de Trabalho em aberto.',
        tone: 'neutral',
        delta: null,
      },
      {
        id: 'avg-progress',
        label: 'Progresso médio',
        value: '63',
        unit: '%',
        hint: 'Média ponderada de Ações concluídas sobre o total planejado.',
        tone: 'success',
        delta: { label: '+7 p.p. vs. período anterior', direction: 'up', isGood: true },
      },
      {
        id: 'delivered',
        label: 'Itens entregues no período',
        value: '134',
        unit: 'itens',
        hint: 'Itens de Trabalho de qualquer nível concluídos nos últimos 30 dias.',
        tone: 'success',
        delta: { label: '+16 vs. período anterior', direction: 'up', isGood: true },
      },
      {
        id: 'blocked',
        label: 'Itens bloqueados',
        value: '7',
        unit: 'itens',
        hint: 'Itens parados aguardando decisão ou dependência externa.',
        tone: 'danger',
        delta: { label: '+3 vs. período anterior', direction: 'up', isGood: false },
      },
    ],
    progressBySolution: [
      { solution: 'Plataforma Orbind', owner: 'Cristian Ramos', done: 148, total: 210, blocked: 3 },
      { solution: 'Portal do Empresa', owner: 'Marina Fontes', done: 62, total: 96, blocked: 2 },
      { solution: 'Motor de Cobrança', owner: 'Paulo Deodato', done: 39, total: 88, blocked: 2 },
      { solution: 'Central de Integrações', owner: 'Renata Alencar', done: 21, total: 54, blocked: 0 },
    ],
    deliveryTrend: {
      title: 'Entregas por semana',
      caption: 'Itens de Trabalho concluídos por semana, somando todos os níveis do portfólio.',
      unit: 'itens',
      points: [
        { label: '08/07', value: 24 },
        { label: '15/07', value: 29 },
        { label: '22/07', value: 22 },
        { label: '29/07', value: 31 },
        { label: '05/08', value: 28 },
      ],
    },
    itemsByLevel: {
      title: 'Itens em aberto por nível do portfólio',
      caption: 'Hierarquia fixa Solução → Produto → Módulo → Funcionalidade → Ação.',
      unit: 'itens',
      bars: [
        { label: 'Solução', value: 4 },
        { label: 'Produto', value: 11 },
        { label: 'Módulo', value: 27 },
        { label: 'Funcionalidade', value: 68 },
        { label: 'Ação', value: 152 },
      ],
    },
  },
}

const SNAPSHOT_90D: AnalyticsSnapshot = {
  periodLabel: 'Últimos 90 dias',
  generatedAtLabel: 'Consolidado em 08/08/2026, 06:00',
  flow: {
    cards: [
      {
        id: 'lead-time',
        label: 'Lead time médio',
        value: '10,1',
        unit: 'dias',
        hint: 'Da criação do Item de Trabalho até a entrada em Concluído.',
        tone: 'warning',
        delta: { label: '−0,8 dia vs. trimestre anterior', direction: 'down', isGood: true },
      },
      {
        id: 'cycle-time',
        label: 'Cycle time médio',
        value: '4,6',
        unit: 'dias',
        hint: 'Do primeiro movimento para Em execução até Concluído.',
        tone: 'neutral',
        delta: { label: '−0,2 dia vs. trimestre anterior', direction: 'down', isGood: true },
      },
      {
        id: 'throughput',
        label: 'Throughput',
        value: '29',
        unit: 'itens/semana',
        hint: 'Média de Itens de Trabalho concluídos por semana no período.',
        tone: 'success',
        delta: { label: '+6 itens vs. trimestre anterior', direction: 'up', isGood: true },
      },
      {
        id: 'wip',
        label: 'Trabalho em andamento',
        value: '46',
        unit: 'itens',
        hint: 'Itens de Trabalho fora de Backlog e de Concluído neste momento.',
        tone: 'warning',
        delta: { label: '+5 itens vs. trimestre anterior', direction: 'up', isGood: false },
      },
    ],
    leadTime: {
      title: 'Lead time por mês',
      caption: 'Média de dias entre a criação e a conclusão dos Itens de Trabalho encerrados no mês.',
      unit: 'd',
      target: { value: 8, label: 'Meta: 8 dias' },
      points: [
        { label: 'Mai', value: 11.4 },
        { label: 'Jun', value: 10.7 },
        { label: 'Jul', value: 9.8 },
        { label: 'Ago', value: 9.4 },
      ],
    },
    throughput: {
      title: 'Itens de Trabalho concluídos por mês',
      caption: 'Contagem de itens que entraram em Concluído em cada mês do período.',
      unit: 'itens',
      bars: [
        { label: 'Mai', value: 108 },
        { label: 'Jun', value: 121 },
        { label: 'Jul', value: 129, tone: 'success' },
        { label: 'Ago', value: 62 },
      ],
    },
    cycleTimeByType: {
      title: 'Cycle time por tipo de Item de Trabalho',
      caption: 'Tempo médio em execução, por tipo, no trimestre.',
      unit: 'd',
      bars: [
        { label: 'Ação', value: 2.1, tone: 'success' },
        { label: 'Funcionalidade', value: 4.8 },
        { label: 'Módulo', value: 11.2, tone: 'warning' },
        { label: 'Correção', value: 2.9, tone: 'success' },
        { label: 'Melhoria', value: 5.6 },
      ],
    },
    wipByStatus: {
      title: 'Trabalho em andamento por status',
      caption: 'Distribuição atual dos Itens de Trabalho que já saíram do Backlog e ainda não foram concluídos.',
      unit: 'itens',
      bars: [
        { label: 'Em análise', value: 11 },
        { label: 'Em execução', value: 19 },
        { label: 'Em revisão', value: 9 },
        { label: 'Bloqueado', value: 7, tone: 'danger' },
      ],
    },
  },
  demands: {
    cards: [
      {
        id: 'sla-compliance',
        label: 'SLA cumprido',
        value: '90',
        unit: '%',
        hint: 'Demandas respondidas dentro do prazo do tipo de serviço.',
        tone: 'warning',
        delta: { label: '+1 p.p. vs. trimestre anterior', direction: 'up', isGood: true },
      },
      {
        id: 'open-demands',
        label: 'Demandas em aberto',
        value: '58',
        unit: 'demandas',
        hint: 'Demandas registradas e ainda não encerradas.',
        tone: 'neutral',
        delta: { label: '+6 vs. trimestre anterior', direction: 'up', isGood: false },
      },
      {
        id: 'triage-time',
        label: 'Tempo médio de triagem',
        value: '5,4',
        unit: 'horas',
        hint: 'Do registro da demanda até a definição de área e prioridade.',
        tone: 'success',
        delta: { label: '−0,9 hora vs. trimestre anterior', direction: 'down', isGood: true },
      },
      {
        id: 'breached',
        label: 'Demandas com SLA estourado',
        value: '34',
        unit: 'demandas',
        hint: 'Demandas que passaram do prazo de resposta no período.',
        tone: 'danger',
        delta: { label: '−7 vs. trimestre anterior', direction: 'down', isGood: true },
      },
    ],
    slaCompliance: {
      title: 'Cumprimento de SLA por mês',
      caption: 'Percentual de demandas respondidas dentro do prazo em cada mês.',
      unit: '%',
      target: { value: 95, label: 'Meta: 95%' },
      points: [
        { label: 'Mai', value: 87 },
        { label: 'Jun', value: 89 },
        { label: 'Jul', value: 91 },
        { label: 'Ago', value: 92 },
      ],
    },
    byChannel: {
      title: 'Demandas por canal de entrada',
      caption: 'Origem das demandas registradas no período.',
      unit: 'demandas',
      bars: [
        { label: 'Portal', value: 218 },
        { label: 'E-mail', value: 126 },
        { label: 'Reunião', value: 54 },
        { label: 'Telefone', value: 38 },
        { label: 'API', value: 22 },
      ],
    },
    byStage: {
      title: 'Demandas por estágio',
      caption: 'Onde estão as demandas em aberto neste momento.',
      unit: 'demandas',
      bars: [
        { label: 'Nova', value: 14 },
        { label: 'Em triagem', value: 16, tone: 'warning' },
        { label: 'Aguardando solicitante', value: 11 },
        { label: 'Em atendimento', value: 13 },
        { label: 'Pronta p/ conversão', value: 4, tone: 'success' },
      ],
    },
    slaByPriority: [
      { priority: 'Crítica', targetLabel: 'Responder em 4 h', total: 57, onTime: 45, breached: 12 },
      { priority: 'Alta', targetLabel: 'Responder em 8 h', total: 138, onTime: 124, breached: 14 },
      { priority: 'Média', targetLabel: 'Responder em 24 h', total: 174, onTime: 167, breached: 7 },
      { priority: 'Baixa', targetLabel: 'Responder em 72 h', total: 89, onTime: 88, breached: 1 },
    ],
  },
  portfolio: {
    cards: [
      {
        id: 'active-solutions',
        label: 'Soluções ativas',
        value: '4',
        unit: 'soluções',
        hint: 'Soluções com pelo menos um Item de Trabalho em aberto.',
        tone: 'neutral',
        delta: null,
      },
      {
        id: 'avg-progress',
        label: 'Progresso médio',
        value: '63',
        unit: '%',
        hint: 'Média ponderada de Ações concluídas sobre o total planejado.',
        tone: 'success',
        delta: { label: '+18 p.p. vs. trimestre anterior', direction: 'up', isGood: true },
      },
      {
        id: 'delivered',
        label: 'Itens entregues no período',
        value: '420',
        unit: 'itens',
        hint: 'Itens de Trabalho de qualquer nível concluídos nos últimos 90 dias.',
        tone: 'success',
        delta: { label: '+62 vs. trimestre anterior', direction: 'up', isGood: true },
      },
      {
        id: 'blocked',
        label: 'Itens bloqueados',
        value: '7',
        unit: 'itens',
        hint: 'Itens parados aguardando decisão ou dependência externa.',
        tone: 'danger',
        delta: { label: '+1 vs. trimestre anterior', direction: 'up', isGood: false },
      },
    ],
    progressBySolution: [
      { solution: 'Plataforma Orbind', owner: 'Cristian Ramos', done: 148, total: 210, blocked: 3 },
      { solution: 'Portal do Empresa', owner: 'Marina Fontes', done: 62, total: 96, blocked: 2 },
      { solution: 'Motor de Cobrança', owner: 'Paulo Deodato', done: 39, total: 88, blocked: 2 },
      { solution: 'Central de Integrações', owner: 'Renata Alencar', done: 21, total: 54, blocked: 0 },
    ],
    deliveryTrend: {
      title: 'Entregas por mês',
      caption: 'Itens de Trabalho concluídos por mês, somando todos os níveis do portfólio.',
      unit: 'itens',
      points: [
        { label: 'Mai', value: 112 },
        { label: 'Jun', value: 126 },
        { label: 'Jul', value: 134 },
        { label: 'Ago', value: 48 },
      ],
    },
    itemsByLevel: {
      title: 'Itens em aberto por nível do portfólio',
      caption: 'Hierarquia fixa Solução → Produto → Módulo → Funcionalidade → Ação.',
      unit: 'itens',
      bars: [
        { label: 'Solução', value: 4 },
        { label: 'Produto', value: 11 },
        { label: 'Módulo', value: 27 },
        { label: 'Funcionalidade', value: 68 },
        { label: 'Ação', value: 152 },
      ],
    },
  },
}

const SNAPSHOT_12M: AnalyticsSnapshot = {
  periodLabel: 'Últimos 12 meses',
  generatedAtLabel: 'Consolidado em 08/08/2026, 06:00',
  flow: {
    cards: [
      {
        id: 'lead-time',
        label: 'Lead time médio',
        value: '12,3',
        unit: 'dias',
        hint: 'Da criação do Item de Trabalho até a entrada em Concluído.',
        tone: 'danger',
        delta: { label: '−2,9 dias vs. ano anterior', direction: 'down', isGood: true },
      },
      {
        id: 'cycle-time',
        label: 'Cycle time médio',
        value: '5,3',
        unit: 'dias',
        hint: 'Do primeiro movimento para Em execução até Concluído.',
        tone: 'warning',
        delta: { label: '−1,1 dia vs. ano anterior', direction: 'down', isGood: true },
      },
      {
        id: 'throughput',
        label: 'Throughput',
        value: '26',
        unit: 'itens/semana',
        hint: 'Média de Itens de Trabalho concluídos por semana no período.',
        tone: 'success',
        delta: { label: '+9 itens vs. ano anterior', direction: 'up', isGood: true },
      },
      {
        id: 'wip',
        label: 'Trabalho em andamento',
        value: '46',
        unit: 'itens',
        hint: 'Itens de Trabalho fora de Backlog e de Concluído neste momento.',
        tone: 'warning',
        delta: { label: '+12 itens vs. ano anterior', direction: 'up', isGood: false },
      },
    ],
    leadTime: {
      title: 'Lead time por trimestre',
      caption: 'Média de dias entre a criação e a conclusão dos Itens de Trabalho encerrados no trimestre.',
      unit: 'd',
      target: { value: 8, label: 'Meta: 8 dias' },
      points: [
        { label: '3T/25', value: 15.2 },
        { label: '4T/25', value: 14.1 },
        { label: '1T/26', value: 12.6 },
        { label: '2T/26', value: 10.9 },
        { label: '3T/26', value: 9.6 },
      ],
    },
    throughput: {
      title: 'Itens de Trabalho concluídos por trimestre',
      caption: 'Contagem de itens que entraram em Concluído em cada trimestre.',
      unit: 'itens',
      bars: [
        { label: '3T/25', value: 246 },
        { label: '4T/25', value: 288 },
        { label: '1T/26', value: 317 },
        { label: '2T/26', value: 355, tone: 'success' },
        { label: '3T/26', value: 182 },
      ],
    },
    cycleTimeByType: {
      title: 'Cycle time por tipo de Item de Trabalho',
      caption: 'Tempo médio em execução, por tipo, nos últimos 12 meses.',
      unit: 'd',
      bars: [
        { label: 'Ação', value: 2.6, tone: 'success' },
        { label: 'Funcionalidade', value: 5.9 },
        { label: 'Módulo', value: 13.4, tone: 'danger' },
        { label: 'Correção', value: 3.2, tone: 'success' },
        { label: 'Melhoria', value: 6.4 },
      ],
    },
    wipByStatus: {
      title: 'Trabalho em andamento por status',
      caption: 'Distribuição atual dos Itens de Trabalho que já saíram do Backlog e ainda não foram concluídos.',
      unit: 'itens',
      bars: [
        { label: 'Em análise', value: 11 },
        { label: 'Em execução', value: 19 },
        { label: 'Em revisão', value: 9 },
        { label: 'Bloqueado', value: 7, tone: 'danger' },
      ],
    },
  },
  demands: {
    cards: [
      {
        id: 'sla-compliance',
        label: 'SLA cumprido',
        value: '86',
        unit: '%',
        hint: 'Demandas respondidas dentro do prazo do tipo de serviço.',
        tone: 'warning',
        delta: { label: '+9 p.p. vs. ano anterior', direction: 'up', isGood: true },
      },
      {
        id: 'open-demands',
        label: 'Demandas em aberto',
        value: '58',
        unit: 'demandas',
        hint: 'Demandas registradas e ainda não encerradas.',
        tone: 'neutral',
        delta: { label: '−21 vs. ano anterior', direction: 'down', isGood: true },
      },
      {
        id: 'triage-time',
        label: 'Tempo médio de triagem',
        value: '7,8',
        unit: 'horas',
        hint: 'Do registro da demanda até a definição de área e prioridade.',
        tone: 'warning',
        delta: { label: '−3,4 horas vs. ano anterior', direction: 'down', isGood: true },
      },
      {
        id: 'breached',
        label: 'Demandas com SLA estourado',
        value: '186',
        unit: 'demandas',
        hint: 'Demandas que passaram do prazo de resposta no período.',
        tone: 'danger',
        delta: { label: '−54 vs. ano anterior', direction: 'down', isGood: true },
      },
    ],
    slaCompliance: {
      title: 'Cumprimento de SLA por trimestre',
      caption: 'Percentual de demandas respondidas dentro do prazo em cada trimestre.',
      unit: '%',
      target: { value: 95, label: 'Meta: 95%' },
      points: [
        { label: '3T/25', value: 78 },
        { label: '4T/25', value: 82 },
        { label: '1T/26', value: 85 },
        { label: '2T/26', value: 89 },
        { label: '3T/26', value: 92 },
      ],
    },
    byChannel: {
      title: 'Demandas por canal de entrada',
      caption: 'Origem das demandas registradas no período.',
      unit: 'demandas',
      bars: [
        { label: 'Portal', value: 861 },
        { label: 'E-mail', value: 512 },
        { label: 'Reunião', value: 203 },
        { label: 'Telefone', value: 164 },
        { label: 'API', value: 78 },
      ],
    },
    byStage: {
      title: 'Demandas por estágio',
      caption: 'Onde estão as demandas em aberto neste momento.',
      unit: 'demandas',
      bars: [
        { label: 'Nova', value: 14 },
        { label: 'Em triagem', value: 16, tone: 'warning' },
        { label: 'Aguardando solicitante', value: 11 },
        { label: 'Em atendimento', value: 13 },
        { label: 'Pronta p/ conversão', value: 4, tone: 'success' },
      ],
    },
    slaByPriority: [
      { priority: 'Crítica', targetLabel: 'Responder em 4 h', total: 231, onTime: 178, breached: 53 },
      { priority: 'Alta', targetLabel: 'Responder em 8 h', total: 548, onTime: 471, breached: 77 },
      { priority: 'Média', targetLabel: 'Responder em 24 h', total: 702, onTime: 653, breached: 49 },
      { priority: 'Baixa', targetLabel: 'Responder em 72 h', total: 337, onTime: 330, breached: 7 },
    ],
  },
  portfolio: {
    cards: [
      {
        id: 'active-solutions',
        label: 'Soluções ativas',
        value: '5',
        unit: 'soluções',
        hint: 'Soluções com pelo menos um Item de Trabalho em aberto no período.',
        tone: 'neutral',
        delta: { label: '+1 vs. ano anterior', direction: 'up', isGood: true },
      },
      {
        id: 'avg-progress',
        label: 'Progresso médio',
        value: '63',
        unit: '%',
        hint: 'Média ponderada de Ações concluídas sobre o total planejado.',
        tone: 'success',
        delta: { label: '+41 p.p. vs. ano anterior', direction: 'up', isGood: true },
      },
      {
        id: 'delivered',
        label: 'Itens entregues no período',
        value: '1.388',
        unit: 'itens',
        hint: 'Itens de Trabalho de qualquer nível concluídos nos últimos 12 meses.',
        tone: 'success',
        delta: { label: '+402 vs. ano anterior', direction: 'up', isGood: true },
      },
      {
        id: 'blocked',
        label: 'Itens bloqueados',
        value: '7',
        unit: 'itens',
        hint: 'Itens parados aguardando decisão ou dependência externa.',
        tone: 'danger',
        delta: { label: '−5 vs. ano anterior', direction: 'down', isGood: true },
      },
    ],
    progressBySolution: [
      { solution: 'Plataforma Orbind', owner: 'Cristian Ramos', done: 148, total: 210, blocked: 3 },
      { solution: 'Portal do Empresa', owner: 'Marina Fontes', done: 62, total: 96, blocked: 2 },
      { solution: 'Motor de Cobrança', owner: 'Paulo Deodato', done: 39, total: 88, blocked: 2 },
      { solution: 'Central de Integrações', owner: 'Renata Alencar', done: 21, total: 54, blocked: 0 },
      { solution: 'Portal Legado (descontinuado)', owner: 'Marina Fontes', done: 74, total: 74, blocked: 0 },
    ],
    deliveryTrend: {
      title: 'Entregas por trimestre',
      caption: 'Itens de Trabalho concluídos por trimestre, somando todos os níveis do portfólio.',
      unit: 'itens',
      points: [
        { label: '3T/25', value: 251 },
        { label: '4T/25', value: 294 },
        { label: '1T/26', value: 322 },
        { label: '2T/26', value: 361 },
        { label: '3T/26', value: 160 },
      ],
    },
    itemsByLevel: {
      title: 'Itens em aberto por nível do portfólio',
      caption: 'Hierarquia fixa Solução → Produto → Módulo → Funcionalidade → Ação.',
      unit: 'itens',
      bars: [
        { label: 'Solução', value: 5 },
        { label: 'Produto', value: 13 },
        { label: 'Módulo', value: 31 },
        { label: 'Funcionalidade', value: 74 },
        { label: 'Ação', value: 168 },
      ],
    },
  },
}

/**
 * Consolidado por periodo.
 *
 * `7d` e propositalmente `null`: o job de consolidacao roda semanalmente, entao
 * a janela de 7 dias ainda nao tem numero fechado. E o caso que exercita o
 * estado vazio da tela com CTA para trocar o periodo.
 */
export const ANALYTICS_BY_PERIOD: Record<AnalyticsPeriodId, AnalyticsSnapshot | null> = {
  '7d': null,
  '30d': SNAPSHOT_30D,
  '90d': SNAPSHOT_90D,
  '12m': SNAPSHOT_12M,
}

/** Consolidado do periodo; `null` quando ainda nao ha dado fechado. */
export function getAnalytics(period: AnalyticsPeriodId): AnalyticsSnapshot | null {
  return ANALYTICS_BY_PERIOD[period]
}

/** Rotulo do periodo para textos da tela. */
export function analyticsPeriodLabel(period: AnalyticsPeriodId): string {
  return ANALYTICS_PERIODS.find((option) => option.value === period)?.label ?? 'Período'
}
